<template>
  <v-form v-model="valid" ref="form" class="fill-width">
    <v-autocomplete
      ref="autocomplete"
      v-model="selectedItems"
      :items="_orderedItems"
      :search-input.sync="search"
      :multiple="multiple"
      :loading="loading"
      :disabled="disabled || loading"
      :required="required"
      :hide-details="hideDetails"
      :hide-no-data="true"
      :placeholder="placeholder"
      :label="label"
      :item-text="itemText"
      :item-value="itemValue"
      :rules="rules"
      :clearable="clearable"
      :filter="() => true"
      :dense="dense"
      background-color="white"
      outlined
      open-on-clear
      return-object
      @focus="onFocus"
      @blur="onBlur"
      @change="onSelectChange"
    >
      <template v-slot:label>
        <template
          v-if="
            !hideAllChip &&
            allIfEmpty &&
            (!selectedItems || selectedItems.length === 0) &&
            !focused
          "
        >
          <div class="d-flex">
            <div class="mr-2">{{ label }}</div>

            <v-chip
              class="mx-0"
              style="margin-top: 2px"
              :color="disabled ? 'gray6' : 'primary'"
              text-color="white"
              x-small
            >
              {{ allLabel }}
            </v-chip>
          </div>
        </template>

        <template v-else>
          {{ label }}
        </template>
      </template>

      <template v-slot:no-data>
        <v-list two-line>
          <v-list-item>
            <v-list-item-title>
              {{ "Nenhum item encontrado..." }}
            </v-list-item-title>
          </v-list-item>

          <v-divider></v-divider>

          <v-list-item v-if="createIfNotFound">
            <v-list-item-title>
              <v-btn
                block
                outlined
                dense
                x-small
                color="primary"
                @click="$emit('create-if-not-found')"
              >
                criar {{ search }}
              </v-btn>
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </template>

      <template v-slot:selection="{ attr, on, item, selected, index }">
        <v-chip
          v-if="!focused && index === 0"
          v-bind="attr"
          :input-value="selected"
          color="primary"
          class="white--text"
          v-on="on"
          small
        >
          <span
            class="item-selector__chip-text"
            :class="{ block }"
            :title="item[itemText] || '--'"
            v-text="item[itemText] || '--'"
          ></span>
        </v-chip>

        <v-tooltip v-else-if="!focused && index === 1" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              v-bind="attrs"
              v-on="on"
              color="primary"
              class="white--text"
              small
            >
              <span v-text="_additionalSelectedValue"></span>
            </v-chip>
          </template>
          <div class="d-flex flex-column">
            <span
              class="caption"
              v-for="(item, i) in _additionalSelectedItems"
              :key="i"
            >
              {{ item[itemText] || item[itemSubText] || "--" }}
            </span>
          </div>
        </v-tooltip>
      </template>

      <template v-slot:item="{ item }">
        <v-list-item-avatar
          v-if="!hideAvatar"
          class="mr-2"
          color="primary"
          size="1.75rem"
        >
          <v-icon x-small class="white--text">
            {{ getInitials(item) }}
          </v-icon>
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title>{{ item[itemText] || "--" }}</v-list-item-title>
          <v-list-item-subtitle v-if="itemSubText">
            {{ getSubtext(item) || "--" }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </template>

      <template v-if="showMore" v-slot:append-item>
        <div class="d-flex justify-center mt-2">
          <v-btn
            class="text-none"
            color="primary"
            :loading="loading"
            :disabled="_orderedItems.length >= count"
            depressed
            small
            @click="loadMoreItems"
          >
            Carregar Mais
          </v-btn>
        </div>
      </template>
    </v-autocomplete>
  </v-form>
</template>

<script>
export default {
  name: "ItemSelector",
  props: {
    value: {
      type: [Array, Object, String],
      required: false,
    },
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    count: {
      type: Number,
      default: 0, // Total de itens disponíveis
    },
    label: {
      type: String,
      default: "Selecione os itens",
    },
    placeholder: {
      type: String,
      default: "Pesquisar...",
    },
    itemText: {
      type: String,
      default: "name",
    },
    itemSubText: {
      type: String,
    },
    itemValue: {
      type: String,
      default: "_id",
    },
    rules: {
      type: Array,
      default: () => [],
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    createIfNotFound: {
      type: Boolean,
      default: false,
    },
    block: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: [String, Boolean],
      default: "auto",
    },
    showMore: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    clearOnBlur: {
      type: Boolean,
      default: true,
    },
    allIfEmpty: {
      type: Boolean,
      default: false,
    },
    allLabel: {
      type: String,
      default: "Todos",
    },
    hideAvatar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valid: false,
      searchInterval: null,
      selectInterval: null,
      search: "",
      focused: false,
      hideAllChip: false,
    };
  },
  watch: {
    search(value, oldValue) {
      if (value === oldValue || (!value && !oldValue)) return;
      if (this.searchInterval) {
        clearTimeout(this.searchInterval);
      }
      this.searchInterval = setTimeout(() => {
        this.$emit("search", value || "");
      }, 500);
    },
    hideAllChip(val) {
      if (val)
        setTimeout(() => {
          this.hideAllChip = false;
        }, 500);
    },
  },
  computed: {
    /** Mantém o v-model externo */
    selectedItems: {
      get() {
        return this.value;
      },
      set(value) {
        if (this.selectInterval) {
          clearTimeout(this.selectInterval);
        }
        this.selectInterval = setTimeout(() => {
          this.$emit("input", value);
        }, 500);
      },
    },

    isEmptySelection() {
      if (this.multiple) {
        return (
          !Array.isArray(this.selectedItems) || this.selectedItems.length === 0
        );
      }
      return !this.selectedItems;
    },

    _orderedItems() {
      const selectedValues = [];
      if (this.selectedItems) {
        selectedValues.push(
          ...[]
            .concat(this.selectedItems)
            .map((item) => item && item[this.itemValue])
            .filter(Boolean)
        );
      }
      const notSelected = this.items.filter(
        (item) => item && !selectedValues.includes(item[this.itemValue])
      );
      const selected = [].concat(this.selectedItems || []);
      return [...selected, ...notSelected];
    },

    _additionalSelectedValue() {
      if (!this.multiple) return;
      return this.selectedItems?.length > 1
        ? `+${this.selectedItems.length - 1}`
        : "";
    },

    _additionalSelectedItems() {
      if (!this.multiple) return;
      return this.selectedItems?.length > 1 ? this.selectedItems.slice(1) : [];
    },
  },
  beforeMount() {
    if (!this.selectedItems) {
      this.selectedItems = this.multiple ? [] : null;
    }
  },
  beforeDestroy() {
    this.resetData();
  },
  methods: {
    getInitials(item) {
      if (!item?.[this.itemText]) {
        if (item?.[this.itemSubText]) {
          return item[this.itemSubText][0];
        }
        return "";
      }
      const onlyText = item[this.itemText]
        .replace(/[^a-zA-Z ]/g, "")
        .replace(/\s+/g, " ");
      const splitted = onlyText.split(" ");
      if (splitted.length > 1) {
        return `${splitted[0][0] || ""}${splitted[1][0] || ""}`.toUpperCase();
      }
      return `${splitted[0][0] || ""}`.toUpperCase();
    },

    resetData() {
      this.search = "";
      this.selectedItems = this.multiple ? [] : null;
    },

    loadMoreItems() {
      this.$emit("paginate", this.search);
    },

    onSelectChange() {
      if (!this.multiple) {
        this.$nextTick(() => {
          this.$refs.autocomplete.blur();
        });
      }
    },

    onFocus() {
      this.focused = true;

      if (!this.clearOnBlur) this.search = "";
    },

    onBlur() {
      this.focused = false;
      this.hideAllChip = true;

      if (this.clearOnBlur && this.search?.length) {
        this.search = "";
      }
    },

    validate() {
      if (!this.rules.length) return true;
      return this.$refs.form.validate();
    },

    resetValidate() {
      this.$refs.form.resetValidate();
    },

    getSubtext(item) {
      if (!this.itemSubText) return "";
      const itemFound = this.items.find(
        (e) => e[this.itemValue] === item[this.itemValue]
      );
      return itemFound ? itemFound[this.itemSubText] || "" : "";
    },
  },
};
</script>

<style scoped lang="scss" src="./style.scss"></style>
